import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";

const UpgradeSuccess = () => {
  const { profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (profile?.subscription_tier !== 'free') {
      setConfirmed(true);
      return;
    }

    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      await refreshProfile();
      if (attempts >= 5) {
        clearInterval(interval);
        setTimedOut(true);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Watch for profile changes from polling
  useEffect(() => {
    if (profile?.subscription_tier && profile.subscription_tier !== 'free') {
      setConfirmed(true);
    }
  }, [profile?.subscription_tier]);

  if (!confirmed && !timedOut) {
    return (
      <>
        <SEOHead title="Welcome to Plus — Dearestly." description="Your Dearestly. Plus upgrade is confirmed." path="/upgrade-success" noindex />
        <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 text-center px-6">
          <Loader2 className="h-10 w-10 animate-spin" style={{ color: "#C9A84C" }} />
          <p className="text-muted-foreground">Activating your Dearestly Plus account…</p>
        </div>
      </div>
    </>
    );
  }

  if (timedOut && !confirmed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 text-center px-6 max-w-md">
          <CheckCircle className="h-16 w-16" style={{ color: "#C9A84C" }} />
          <h1 className="font-serif text-2xl font-bold text-foreground">Payment received</h1>
          <p className="text-muted-foreground">
            Your Dearestly Plus account will be active within a few minutes. You can start using Dearestly right away.
          </p>
          <Button
            className="mt-4 rounded-full px-8 py-6 text-base font-semibold"
            style={{ backgroundColor: "#C9A84C", color: "#2D2A26" }}
            onClick={() => navigate("/dashboard")}
          >
            Open Dearestly →
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-6 text-center px-6 max-w-md animate-fade-in">
        <div
          className="flex h-20 w-20 items-center justify-center rounded-full"
          style={{ backgroundColor: "rgba(201, 168, 76, 0.15)" }}
        >
          <CheckCircle className="h-10 w-10" style={{ color: "#C9A84C" }} />
        </div>

        <h1 className="font-serif text-3xl font-bold text-foreground">
          Welcome to Dearestly Plus
        </h1>
        <p className="text-muted-foreground">
          Your relationship memory just got a lot richer.
        </p>

        <div className="flex flex-col gap-3 w-full mt-2">
          {[
            "Unlimited contacts",
            "All occasion types",
            "Unlimited messages",
          ].map((line) => (
            <div key={line} className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
              <CheckCircle className="h-5 w-5 shrink-0" style={{ color: "#C9A84C" }} />
              <span className="text-sm font-medium text-foreground">{line}</span>
            </div>
          ))}
        </div>

        <Button
          className="mt-4 w-full rounded-full py-6 text-base font-semibold"
          style={{ backgroundColor: "#C9A84C", color: "#2D2A26" }}
          onClick={() => navigate("/dashboard")}
        >
          Open Dearestly →
        </Button>
      </div>
    </div>
  );
};

export default UpgradeSuccess;
